import type { ContentType } from "@brookbush/database";
import type { Highlight } from "@brookbush/types";

import {
	memo,
	type PropsWithChildren,
	useEffect,
	useRef,
	useState,
} from "react";

import AnnotationsProviderHighlights from "./AnnotationsProviderHighlights";
import AnnotationsProviderTooltip from "./AnnotationsProviderTooltip";
import useHighlights from "~/hooks/useHighlights";
import { resolveAnnotationsRootId } from "~/lib/annotations/resolveAnnotationsRootId";
import { shouldUpdateAnnotationRange } from "~/lib/annotations/shouldUpdateAnnotationRange";

export type AnnotationsProps = {
	highlight?: boolean;
	askAI?: boolean;
	contentId: string;
	contentType: ContentType;
	contentKey?: string;
};

export type AnnotationSelection = {
	content: string;
	range: Range;
};

const AnnotationsProvider = ({
	children,
	contentKey,
	...options
}: PropsWithChildren<AnnotationsProps>) => {
	const { askAI, highlight, contentId } = options;
	const rootId = resolveAnnotationsRootId(contentKey, contentId);

	const ref = useRef<HTMLDivElement>(null);
	const [range, setRange] = useState<Range | null>(null);
	const rangeTextRef = useRef<string | null>(null);

	const [activeHighlight, setActiveHighlight] = useState<Highlight | null>(
		null,
	);

	const { highlights } = useHighlights(options);

	useEffect(() => {
		rangeTextRef.current = range?.toString() ?? null;
	}, [range]);

	useEffect(() => {
		const handler = () => {
			const selection = document.getSelection();

			const active = document.activeElement;

			// Ignore if focus is in a textarea or input
			if (
				active &&
				(active.tagName === "TEXTAREA" || active.tagName === "INPUT")
			) {
				return;
			}

			if (!askAI && !highlight) {
				return;
			}

			const isAllowedToAnnotate = Boolean(
				selection?.anchorNode &&
					document.getElementById(rootId)?.contains(selection.anchorNode),
			);

			const nextText =
				!selection || selection.isCollapsed || !isAllowedToAnnotate
					? null
					: selection.toString();

			if (
				!shouldUpdateAnnotationRange({
					previousText: rangeTextRef.current,
					nextText,
				})
			) {
				return;
			}

			rangeTextRef.current = nextText;

			if (nextText === null) {
				setRange(null);
				setActiveHighlight(null);
				return;
			}

			setRange(selection!.getRangeAt(0));
		};

		document.addEventListener("selectionchange", handler);
		return () => document.removeEventListener("selectionchange", handler);
	}, [askAI, highlight, rootId]);

	return (
		<div ref={ref} id={rootId} className="relative w-full">
			<div className="relative z-10">{children}</div>

			{highlights && (
				<AnnotationsProviderHighlights
					parentRef={ref}
					highlights={highlights}
					setActiveHighlight={setActiveHighlight}
					activeHighlight={activeHighlight}
					setRange={setRange}
				/>
			)}

			<AnnotationsProviderTooltip
				range={range}
				setRange={setRange}
				options={options}
				parentRef={ref}
				setActiveHighlight={setActiveHighlight}
				activeHighlight={activeHighlight}
			/>
		</div>
	);
};

export default memo(AnnotationsProvider);
